import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Truck, Search, Power, Loader2, X, Check, Ban, Clock, KeyRound } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ImageUpload, { resolveImageUrl } from '../components/ImageUpload';
import DriverInviteQR from '../components/DriverInviteQR';
import DriverPushAuditModal from '../components/DriverPushAuditModal';

const empty = { name: '', phone: '', email: '', vehicle: '', photo: '', password: '', active: true };

const AdminDrivers = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [drivers, setDrivers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {id?, ...}
  const [saving, setSaving] = useState(false);
  const [, setNowTick] = useState(0); // re-render every 15s so online/offline pills update without a refetch
  const [auditDriver, setAuditDriver] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/drivers`, { headers: authHeaders });
      setDrivers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Initial load only — auto-refresh disabled per user request. Live polling
  // is reserved for the public Live Stock report only.
  useEffect(() => {
    load();
    // One-shot category list for the auto-assignment picker. Uses the
    // admin endpoint so drivers can be auto-routed by hidden / internal
    // categories too (not just storefront-visible ones).
    axios
      .get(`${API}/admin/categories`, { headers: authHeaders })
      .then(({ data }) => setCategories(data || []))
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.name || !editing.phone) {
      toast({ title: 'Name and phone are required' });
      return;
    }
    if (!editing.id && !editing.password) {
      toast({ title: 'Password required for new driver' });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const payload = { ...editing };
        if (!payload.password) delete payload.password;
        delete payload.id;
        await axios.put(`${API}/admin/drivers/${editing.id}`, payload, { headers: authHeaders });
        toast({ title: 'Driver updated' });
      } else {
        await axios.post(`${API}/admin/drivers`, editing, { headers: authHeaders });
        toast({ title: 'Driver added' });
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete driver ${d.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${d.id}`, { headers: authHeaders });
      toast({ title: 'Driver removed' });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed' });
    }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API}/admin/drivers/${d.id}`, { active: !d.active }, { headers: authHeaders });
      await load();
    } catch {
      toast({ title: 'Update failed' });
    }
  };

  // Generates a fresh temp password server-side and pops the plaintext into
  // a copy-once dialog so the admin can hand it to the driver (also emailed).
  const resetPassword = async (d) => {
    if (!window.confirm(`Generate a new temp password for ${d.name}?\nThe driver will be emailed and the current password will stop working.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/drivers/${d.id}/reset-password`, {}, { headers: authHeaders });
      window.prompt(
        `Driver ${data.driver_name} (${data.driver_email}) — new temp password.\nCopy this NOW — it won't be shown again. The driver was emailed too.`,
        data.temp_password,
      );
      toast({ title: 'Password reset', description: `${data.driver_email}` });
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || err.message });
    }
  };

  const setStatus = async (d, status) => {
    const verb = status === 'approved' ? 'Approve' : status === 'rejected' ? 'Reject' : 'Update';
    if (!window.confirm(`${verb} driver ${d.name}?`)) return;
    try {
      await axios.put(
        `${API}/admin/drivers/${d.id}`,
        { status, active: status === 'approved' ? true : d.active },
        { headers: authHeaders },
      );
      toast({ title: `Driver ${status}`, description: status === 'approved' ? `${d.name} can now sign in` : '' });
      await load();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const onlineWindow = 5 * 60 * 1000;
  const isOnline = (d) => d.last_seen && Date.now() - new Date(d.last_seen).getTime() < onlineWindow;

  const filtered = drivers
    .filter(
      (d) =>
        !search ||
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.phone.includes(search) ||
        (d.vehicle || '').toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      // pending first, rejected last
      const rank = (d) => (d.status === 'pending' ? 0 : d.status === 'rejected' ? 2 : 1);
      return rank(a) - rank(b);
    });

  const pendingCount = drivers.filter((d) => d.status === 'pending').length;

  return (
    <>
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">Drivers</h1>
            <p className="text-neutral-500 mt-1">Manage delivery fleet and credentials.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <DriverInviteQR variant="button-only" />
            <Button
              onClick={() => setEditing({ ...empty })}
              className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
              data-testid="add-driver-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add driver
            </Button>
          </div>
        </div>

        {pendingCount > 0 && (
          <div
            className="mb-5 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-3 flex items-center gap-3"
            data-testid="pending-drivers-banner"
          >
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>{pendingCount}</strong> driver{pendingCount > 1 ? 's are' : ' is'} waiting for your review. Approve or reject the application{pendingCount > 1 ? 's' : ''} below.
            </div>
          </div>
        )}

        <div className="relative max-w-md mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or vehicle"
            className="pl-9 rounded-full h-10"
            data-testid="driver-search"
          />
        </div>

        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
            <Truck className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500">No drivers yet. Add your first driver to start dispatching orders.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="drivers-list">
            {filtered.map((d) => {
              const online = isOnline(d);
              return (
                <div key={d.id} className={`bg-white rounded-2xl border p-5 ${
                  (d.status === 'pending') ? 'border-amber-300 ring-2 ring-amber-100' :
                  (d.status === 'rejected') ? 'border-red-200 bg-red-50/40' :
                  'border-neutral-200'
                }`} data-testid={`driver-card-${d.id}`}>
                  {d.status === 'pending' && (
                    <div className="-mt-2 -mx-2 mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                      <Clock className="w-3 h-3" /> Pending review
                    </div>
                  )}
                  {d.status === 'rejected' && (
                    <div className="-mt-2 -mx-2 mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wider">
                      <Ban className="w-3 h-3" /> Rejected
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    {d.photo ? (
                      <img src={resolveImageUrl(d.photo)} alt={d.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-bold">
                        {d.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0F172A] truncate">{d.name}</div>
                      <div className="text-xs text-neutral-500">{d.vehicle || 'No vehicle set'}</div>
                    </div>
                    {(d.status || 'approved') === 'approved' && (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                          !d.active
                            ? 'bg-neutral-200 text-neutral-600'
                            : online
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {!d.active ? 'Disabled' : online ? 'Online' : 'Offline'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-700 space-y-1">
                    <div>{d.phone}</div>
                    {d.email && <div className="text-neutral-500 text-xs">{d.email}</div>}
                    <div
                      className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full cursor-pointer hover:brightness-95 ${
                        d.push_subscribed
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                      title={d.push_subscribed
                        ? 'Driver will receive lock-screen alerts. Click to view delivery log.'
                        : 'Driver has NOT enabled push. Click to view past delivery attempts.'
                      }
                      onClick={() => setAuditDriver(d)}
                      data-testid={`driver-push-status-${d.id}`}
                      role="button"
                      tabIndex={0}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${d.push_subscribed ? 'bg-blue-500' : 'bg-red-500'}`} />
                      {d.push_subscribed ? 'Push on' : 'Push off'} · log
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {d.status === 'pending' && (
                      <>
                        <button
                          onClick={() => setStatus(d, 'approved')}
                          className="text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 px-3 py-1.5 rounded-full flex items-center gap-1"
                          data-testid={`approve-driver-${d.id}`}
                        >
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => setStatus(d, 'rejected')}
                          className="text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200 px-3 py-1.5 rounded-full flex items-center gap-1"
                          data-testid={`reject-driver-${d.id}`}
                        >
                          <Ban className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                    {d.status === 'rejected' && (
                      <button
                        onClick={() => setStatus(d, 'approved')}
                        className="text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full flex items-center gap-1"
                        data-testid={`reapprove-driver-${d.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Approve anyway
                      </button>
                    )}
                    <button
                      onClick={() => setEditing({ ...d, password: '' })}
                      className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-3 py-1.5 rounded-full flex items-center gap-1"
                      data-testid={`edit-driver-${d.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    {(d.status || 'approved') === 'approved' && (
                      <button
                        onClick={() => toggleActive(d)}
                        className="text-xs font-semibold text-neutral-700 hover:bg-neutral-100 px-3 py-1.5 rounded-full flex items-center gap-1"
                        data-testid={`toggle-driver-${d.id}`}
                      >
                        <Power className="w-3.5 h-3.5" /> {d.active ? 'Disable' : 'Enable'}
                      </button>
                    )}
                    <button
                      onClick={() => resetPassword(d)}
                      className="text-xs font-semibold text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-full flex items-center gap-1"
                      data-testid={`reset-driver-pw-${d.id}`}
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Reset password
                    </button>
                    <button
                      onClick={() => remove(d)}
                      className="ml-auto text-xs font-semibold text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-full flex items-center gap-1"
                      data-testid={`delete-driver-${d.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#0F172A]">
                {editing.id ? 'Edit driver' : 'Add driver'}
              </h2>
              <button onClick={() => setEditing(null)} className="text-neutral-400 hover:text-neutral-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { k: 'name', label: 'Full name *' },
                { k: 'phone', label: 'Phone *' },
                { k: 'email', label: 'Email' },
                { k: 'vehicle', label: 'Vehicle (e.g. Van — A12345)' },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">{f.label}</label>
                  <Input
                    value={editing[f.k] || ''}
                    onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })}
                    className="mt-1.5 h-11 rounded-xl"
                    data-testid={`driver-field-${f.k}`}
                  />
                </div>
              ))}

              {/* Auto-assignment categories — controls which orders are
                  automatically routed to this driver. */}
              <div data-testid="driver-field-categories">
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                  Delivery divisions
                </label>
                <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">
                  Orders that contain only items from the selected categories will be auto-assigned to this driver. Leave empty to make them a generalist (delivers any product).
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((c) => {
                    const arr = editing.categories || [];
                    const checked = arr.includes(c.slug);
                    return (
                      <label
                        key={c.slug}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition select-none ${
                          checked
                            ? 'bg-[#DEECF9] border-[#0078D4] text-[#0F172A]'
                            : 'bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300'
                        }`}
                        data-testid={`driver-cat-${c.slug}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? arr.filter((s) => s !== c.slug) : [...arr, c.slug];
                            setEditing({ ...editing, categories: next });
                          }}
                          className="w-4 h-4 accent-[#0078D4]"
                        />
                        <span className="text-sm font-semibold truncate">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                  Driver photo
                </label>
                <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">
                  Shown to the customer on the live tracking page during delivery — boosts trust and recognition at the door.
                </p>
                <ImageUpload
                  value={editing.photo || ''}
                  onChange={(url) => setEditing({ ...editing, photo: url })}
                  testId="driver-field-photo"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-neutral-700 uppercase tracking-wide">
                  Password {editing.id ? '(leave blank to keep)' : '*'}
                </label>
                <Input
                  type="text"
                  value={editing.password || ''}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl font-mono"
                  data-testid="driver-field-password"
                />
                <p className="text-xs text-neutral-500 mt-1.5 flex items-start gap-1">
                  <span className="text-emerald-600">✉</span>
                  <span>
                    {editing.id
                      ? 'If you set a new password, the driver will be emailed the updated credentials (requires their email on file).'
                      : 'When you save, the driver will receive a welcome email with these credentials and a login link (requires their email).'}
                  </span>
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="driver-field-active"
                />
                <span className="text-sm text-neutral-700">Active (driver can log in & receive orders)</span>
              </label>
            </div>
            <div className="p-6 border-t border-neutral-100 flex justify-end gap-2">
              <Button onClick={() => setEditing(null)} variant="ghost" className="rounded-full">
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white"
                data-testid="driver-save"
              >
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Save driver
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
      {auditDriver && (
        <DriverPushAuditModal
          driverId={auditDriver.id}
          driverName={auditDriver.name}
          authHeaders={authHeaders}
          onClose={() => setAuditDriver(null)}
        />
      )}
    </>
  );
};

export default AdminDrivers;
